import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Input,
  Text,
  IconButton,
  useColorModeValue,
  Flex,
  Spinner,
  Collapse,
} from '@chakra-ui/react';
import { FaComments, FaPaperPlane, FaTimes, FaRobot, FaUser } from 'react-icons/fa';
import { getOrCreateEshopSessionToken } from '../services/eshopApi';

interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

const RobotIcon: React.ComponentType<any> = FaRobot as any;
const TimesIcon: React.ComponentType<any> = FaTimes as any;
const PaperPlaneIcon: React.ComponentType<any> = FaPaperPlane as any;
const CommentsIcon: React.ComponentType<any> = FaComments as any;

const SupportChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const chatBg = useColorModeValue('gray.50', 'gray.900');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch('/api/v1/eshop/support/chat/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': getOrCreateEshopSessionToken(),
        },
        body: JSON.stringify({
          message: userMessage,
          history: messages.filter(m => m.role !== 'system').slice(-10),
        }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      if (!response.body) return;

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = '';

      // Add placeholder for assistant message
      setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') break;
            
            try {
              // DeepSeek streaming format: {"id":..., "choices":[{"delta":{"content":"..."}}]}
              // BUT our backend just forwards raw content chunks or JSON?
              // Let's check backend implementation: it forwards JSON chunks from DeepSeek
              const json = JSON.parse(data);
              const content = json.choices?.[0]?.delta?.content || '';
              if (content) {
                assistantMessage += content;
                setMessages((prev) => {
                  const newMessages = [...prev];
                  newMessages[newMessages.length - 1].content = assistantMessage;
                  return newMessages;
                });
              }
            } catch (e) {
              // If backend sends raw text or error
              console.error('Error parsing stream chunk', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages((prev) => [
        ...prev,
        {
          role: 'system',
          content:
            'Omlouvám se, ale došlo k chybě při komunikaci s asistentem. Zkuste to prosím později nebo napište na klubový e‑mail.',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box position="fixed" bottom="20px" right="20px" zIndex={1000}>
      <Collapse in={isOpen} animateOpacity>
        <Box
          bg={bg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="lg"
          boxShadow="xl"
          w="350px"
          h="500px"
          mb={4}
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          {/* Header */}
          <Flex
            bg="blue.600"
            color="white"
            p={3}
            justify="space-between"
            align="center"
          >
            <HStack>
              <RobotIcon />
              <Text fontWeight="bold">MyClub Asistent</Text>
            </HStack>
            <IconButton
              aria-label="Close chat"
              icon={<TimesIcon />}
              size="sm"
              variant="ghost"
              colorScheme="whiteAlpha"
              onClick={() => setIsOpen(false)}
            />
          </Flex>

          {/* Messages */}
          <VStack
            flex={1}
            overflowY="auto"
            p={3}
            spacing={3}
            align="stretch"
            bg={chatBg}
          >
            {messages.length === 0 && (
              <Box textAlign="center" color="gray.500" mt={10}>
                <Box as={RobotIcon} fontSize="40px" mb={2} />
                <Text fontSize="sm">
                  Ahoj! Jsem virtuální asistent MyClub e‑shopu. <br />
                  Mohu poradit s produkty, dostupností, dopravou nebo stavem objednávky.
                </Text>
              </Box>
            )}
            {messages.map((msg, idx) => {
              const isUser = msg.role === 'user';
              const isSystem = msg.role === 'system';
              const bubbleBg = isUser ? 'blue.500' : isSystem ? 'red.500' : 'white';
              const bubbleColor = isUser || isSystem ? 'white' : 'black';

              return (
                <Flex
                  key={idx}
                  justify={isUser ? 'flex-end' : 'flex-start'}
                >
                  <Box
                    maxW="80%"
                    bg={bubbleBg}
                    color={bubbleColor}
                    px={3}
                    py={2}
                    borderRadius="lg"
                    boxShadow="sm"
                    borderTopRightRadius={isUser ? 0 : 'lg'}
                    borderTopLeftRadius={!isUser ? 0 : 'lg'}
                  >
                    <Text fontSize="sm" whiteSpace="pre-wrap">
                      {msg.content}
                    </Text>
                  </Box>
                </Flex>
              );
            })}
            {isLoading && (
              <Flex justify="flex-start">
                <Box bg="white" px={3} py={2} borderRadius="lg" boxShadow="sm">
                  <Spinner size="xs" color="gray.500" />
                </Box>
              </Flex>
            )}
            <div ref={messagesEndRef} />
          </VStack>

          {/* Input */}
          <Box p={3} borderTop="1px solid" borderColor={borderColor}>
            <HStack>
              <Input
                placeholder="Napište zprávu..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                size="sm"
                borderRadius="full"
              />
              <IconButton
                aria-label="Send"
                icon={<PaperPlaneIcon />}
                size="sm"
                colorScheme="blue"
                borderRadius="full"
                onClick={handleSend}
                isLoading={isLoading}
              />
            </HStack>
          </Box>
        </Box>
      </Collapse>

      {!isOpen && (
        <Button
          leftIcon={<CommentsIcon />}
          colorScheme="blue"
          size="lg"
          borderRadius="full"
          boxShadow="lg"
          onClick={() => setIsOpen(true)}
        >
          Podpora
        </Button>
      )}
    </Box>
  );
};

export default SupportChatWidget;
